import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { ITheme, IThemeBreakpointSpecification } from '@inter-ikea-kompis/types';

/**
 * Class providing font styles depending on font style.
 */
export default class FontStyle {
	/**
	 * Returns font styles.
	 *
	 * @param options Options.
	 * @param options.theme Theme.
	 * @param options.type "title76", "title60" etc.
	 * @param [options.bold] Bold.
	 * @param [options.italic] Italic.
	 * @param [options.underline] Underline.
	 * @param [options.querySelector] Defaults to ":host".
	 * @returns CSS code.
	 */
	public static getStyle(options: {
		theme: ITheme;
		type: ThemeFontStyleTypeEnum;
		bold?: boolean;
		italic?: boolean;
		underline?: boolean;
		querySelector?: string;
	}): string {
		const querySelector = options.querySelector || ':host';
		// "small" is the default style, so therefore we want to always add it.
		return `
			${this.getComment(options)}
			${querySelector} {
				font-family: ${this.getFontFamily(options.theme)};
				${this.getTextStyle({
					...options,
					breakpoint: options.theme.breakpoint.small
				})}
			}
			${this.getMediaQueryStyles(options)}
		`;
	}

	/**
	 * Returns font styles.
	 *
	 * @param theme Theme.
	 * @returns Font family.
	 */
	public static getFontFamily(theme: ITheme): string {
		const fontTypes = ['sans-serif'];
		return [theme.fontFace.family, ...theme.fontFace.fallbacks]
			.map((font) => (fontTypes.includes(font) ? font : `'${font}'`))
			.join(',');
	}

	/**
	 * Returns media queries.
	 *
	 * @param options Options.
	 * @param options.theme Theme.
	 * @param options.type "bodySmall", "bodySmall" etc.
	 * @param [options.bold] Bold.
	 * @param [options.italic] Italic.
	 * @param [options.underline] Underline.
	 * @param [options.querySelector] Defaults to ":host".
	 * @returns CSS code.
	 */
	private static getMediaQueryStyles(options: {
		theme: ITheme;
		type: ThemeFontStyleTypeEnum;
		bold?: boolean;
		italic?: boolean;
		underline?: boolean;
		querySelector?: string;
	}): string {
		const breakpoints = Object.values(options.theme.breakpoint).sort((a, b) => a.width - b.width);
		const querySelector = options.querySelector || ':host';
		const mediaQueries: string[] = [];

		let previousStyle = this.getTextStyle({
			...options,
			breakpoint: options.theme.breakpoint.small
		});

		for (const breakpoint of breakpoints) {
			// We want to exclude "small" as it is the default style, which is added in getStyle().
			if (breakpoint !== options.theme.breakpoint.small) {
				const style = this.getTextStyle({ ...options, breakpoint });
				if (previousStyle !== style) {
					previousStyle = style;
					mediaQueries.push(`
			@media screen and (min-width: ${breakpoint.width / 16}rem) {
				${querySelector} {
					${style}
				}
			}
					`);
				}
			}
		}
		return mediaQueries.join('\n');
	}

	/**
	 * Returns text style.
	 *
	 * @param options Options.
	 * @param options.theme Theme.
	 * @param options.breakpoint Breakpoint.
	 * @param options.type "bodySmall", "bodySmall" etc.
	 * @param [options.bold] Bold.
	 * @param [options.italic] Italic.
	 * @param [options.underline] Underline.
	 * @param options.querySelector
	 * @returns CSS code.
	 */
	private static getTextStyle(options: {
		theme: ITheme;
		breakpoint: IThemeBreakpointSpecification;
		type: ThemeFontStyleTypeEnum;
		bold?: boolean;
		italic?: boolean;
		underline?: boolean;
		querySelector?: string;
	}): string {
		if (!ThemeFontStyleTypeEnum[options.type]) {
			throw new Error(`Failed to generate font style. The type "${options.type}" does not exist.`);
		}

		const font = options.breakpoint.fontStyle[options.type];

		const styles = [`font-size: ${font.size / 16}rem;`, `line-height: ${font.lineHeight / 16}rem;`];

		if (font.letterSpacing) {
			styles.push(`letter-spacing: ${font.letterSpacing / 16}rem;`);
		}

		if (options.bold || font.weight !== undefined) {
			styles.push(`font-weight: ${options.bold ? 'bold' : font.weight};`);
		}

		if (options.italic) {
			styles.push(`font-style: italic;`);
		}

		if (options.underline) {
			styles.push(`text-decoration: underline;`);
		}

		return styles.join('\n\t\t\t\t');
	}

	/**
	 * Returns a CSS comment that describes what options that are applied.
	 *
	 * @param options Options.
	 * @param options.type "title76", "title60" etc.
	 * @param options.bold Bold.
	 * @param options.italic Italic.
	 * @param options.underline Underline.
	 * @returns CSS code.
	 */
	private static getComment(options: {
		type: ThemeFontStyleTypeEnum;
		bold?: boolean;
		italic?: boolean;
		underline?: boolean;
	}): string {
		let comment = `\t\t\t  Generated by @inter-ikea-kompis/themes/lib/utilities/FontStyle.ts:\n\t\t\t  type: ${options.type}`;
		for (const option of ['bold', 'italic', 'underline']) {
			if (options[option]) {
				comment += `\n\t\t\t  ${option}: ${options[option]}`;
			}
		}
		return `/*\n${comment}\n\t\t\t*/`;
	}
}
